import { useCallback, useEffect, useState } from "react";
import {
  adminInBody,
  adminRowing,
  adminStats,
  adminTasks,
  adminTaskSave,
  adminWinners,
  getLeaderboard,
  type AdminInBodyRow,
  type AdminRowingRow,
  type AdminStats,
  type AdminTask,
  type StaffRole,
  type WinnerRow,
} from "../lib/api";
import StoryReview from "../components/StoryReview";
import RefreshButton from "../components/RefreshButton";
import LangPills from "../components/LangPills";
import { clearStaffCode } from "../lib/api";
import { useStaffRole } from "../components/staffRoleContext";
import { useT } from "../i18n";
import type { TFn } from "../i18n";
import { errorMessage } from "../i18n/errors";

const ROLE_OPTIONS: StaffRole[] = ["marketing", "coach", "redbull", "admin"];

function RoleSelect({ value, onChange, t }: { value: StaffRole; onChange: (r: StaffRole) => void; t: TFn }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as StaffRole)}
      className="bg-input text-black rounded-[12px] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet"
    >
      {ROLE_OPTIONS.map((r) => (
        <option key={r} value={r}>{t(`staff.role.${r}`)}</option>
      ))}
    </select>
  );
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-card border border-line-2 rounded-[18px] p-5">
      <div className="text-muted text-sm">{label}</div>
      <div className="text-3xl font-extrabold mt-1 font-display">{value}</div>
      {sub && <div className="text-muted2 text-xs mt-1">{sub}</div>}
    </div>
  );
}

function StatsTab() {
  const { t, lang } = useT();
  const [s, setS] = useState<AdminStats | null>(null);
  // null — бэк ещё не отдаёт /admin/inbody: показываем подсказку вместо списка.
  const [inbody, setInbody] = useState<AdminInBodyRow[] | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setS(await adminStats());
      setError("");
    } catch (e: any) {
      setError(errorMessage(e, t, "admin.task.errLoad"));
    }
    try {
      setInbody((await adminInBody()).results);
    } catch {
      setInbody(null);
    }
  }, [t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  if (error && !s) return <p className="text-red-400 text-sm">{error}</p>;
  if (!s) return <p className="text-muted text-sm">…</p>;

  const top = s.rowing.top;
  return (
    <div className="flex flex-col gap-6">
      <button
        type="button"
        onClick={load}
        className="self-start h-10 px-4 rounded-[12px] bg-white/[0.06] text-white text-sm font-medium active:bg-white/10"
      >
        {t("admin.refresh")}
      </button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Stat
          label={t("admin.stat.participants")}
          value={s.participants.total}
          sub={t("admin.stat.menWomen", { men: s.participants.men, women: s.participants.women, other: s.participants.unspecified })}
        />
        <Stat
          label={t("admin.stat.clans")}
          value={s.clans.total}
          sub={t("admin.stat.clansSub", { ready: s.clans.ready, completed: s.clans.completed })}
        />
        <Stat label={t("admin.stat.members")} value={s.members.total} />
        <Stat
          label={t("admin.stat.rowing")}
          value={s.rowing.count}
          sub={
            t("admin.stat.rowingSub", { prize: s.rowing.withPrize }) +
            (top ? " · " + t("admin.stat.top", { name: top.nickname || "—", power: top.power_w }) : "")
          }
        />
        <Stat label={t("admin.stat.prizes")} value={s.prizes.total} />
      </div>

      {/* Прошедшие InBody (Зона 2) */}
      <section>
        <h2 className="text-lg font-extrabold mb-3">
          {t("admin.inbody.title")}
          {inbody && <span className="ml-2 text-violet-lighter">{inbody.length}</span>}
        </h2>
        {inbody === null ? (
          <p className="text-muted text-sm">{t("admin.inbody.unavailable")}</p>
        ) : (
          <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
            {inbody.map((r, i) => (
              <div key={`${r.phone}-${i}`} className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.nickname || "—"}</div>
                  {r.phone ? (
                    <a href={`tel:${r.phone}`} className="text-xs text-violet-lighter">{r.phone}</a>
                  ) : (
                    <span className="text-xs text-muted">—</span>
                  )}
                </div>
                {r.testDate && (
                  <span className="text-xs text-muted shrink-0">
                    {new Date(r.testDate).toLocaleString(lang === "kk" ? "kk-KZ" : lang === "en" ? "en-US" : "ru-RU", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
                {r.score != null && (
                  <span className="text-sm font-semibold w-12 text-right">{Math.round(r.score)}</span>
                )}
              </div>
            ))}
            {inbody.length === 0 && <div className="p-4 text-muted text-sm">{t("admin.inbody.empty")}</div>}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-extrabold mb-3">{t("admin.stat.tasksTitle")}</h2>
        <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
          {s.tasks.map((task) => (
            <div key={task.key} className="flex items-center gap-3 p-4">
              <span className="flex-1 text-sm">{task.title}</span>
              {task.role && <span className="text-[11px] text-muted rounded-full bg-white/[0.06] px-2 py-1">{t(`staff.role.${task.role}`)}</span>}
              <span className="text-sm font-semibold w-24 text-right">{t("admin.stat.clansDone", { n: task.clansDone })}</span>
            </div>
          ))}
          {s.tasks.length === 0 && <div className="p-4 text-muted text-sm">{t("admin.empty")}</div>}
        </div>
      </section>
    </div>
  );
}

function TaskEditor({ task, onSaved }: { task: AdminTask; onSaved: () => void }) {
  const { t } = useT();
  const [title, setTitle] = useState(task.title);
  const [role, setRole] = useState<StaffRole>((task.role ?? "admin") as StaffRole);
  const [sort, setSort] = useState(String(task.sort));
  const [active, setActive] = useState(task.is_active);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (!title.trim()) return;
    setBusy(true);
    setError("");
    try {
      await adminTaskSave({ key: task.key, title: title.trim(), role, sort: Number(sort), is_active: active });
      onSaved();
    } catch (e: any) {
      setError(errorMessage(e, t, "admin.task.errSave"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`bg-card border rounded-[16px] p-4 flex flex-col gap-3 ${active ? "border-line-2" : "border-line-2 opacity-60"}`}>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full bg-input text-black rounded-[12px] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet"
      />
      <div className="flex items-center gap-3 flex-wrap">
        <RoleSelect value={role} onChange={setRole} t={t} />
        <label className="flex items-center gap-1.5 text-sm text-muted">
          {t("admin.task.sort")}
          <input
            type="number"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-16 bg-input text-black rounded-[10px] px-2 py-1.5 text-sm outline-none"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          {active ? t("admin.task.active") : t("admin.task.off")}
        </label>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="ml-auto h-9 px-4 rounded-[12px] bg-violet-grad text-white font-bold text-sm active:opacity-90 disabled:opacity-60 shadow-violet-btn"
        >
          {busy ? t("admin.task.saving") : t("admin.task.save")}
        </button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

function TasksTab() {
  const { t } = useT();
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [error, setError] = useState("");
  // create form
  const [nt, setNt] = useState("");
  const [nr, setNr] = useState<StaffRole>("coach");
  const [na, setNa] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminTasks();
      setTasks(r.tasks);
      setError("");
    } catch (e: any) {
      setError(errorMessage(e, t, "admin.task.errLoad"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function create() {
    if (!nt.trim()) return;
    setCreating(true);
    setError("");
    try {
      await adminTaskSave({ title: nt.trim(), role: nr, is_active: na });
      setNt("");
      await load();
    } catch (e: any) {
      setError(errorMessage(e, t, "admin.task.errSave"));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="bg-card-2 border border-line-3 rounded-[18px] p-5 flex flex-col gap-3">
        <h2 className="text-lg font-extrabold">{t("admin.task.newTitle")}</h2>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-white/80">{t("admin.task.titleLabel")}</span>
          <input
            value={nt}
            onChange={(e) => setNt(e.target.value)}
            placeholder={t("admin.task.titlePh")}
            className="w-full bg-input text-black rounded-[12px] px-3 py-2.5 text-sm placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
          />
        </label>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-white/80">
            {t("admin.task.roleLabel")}
            <RoleSelect value={nr} onChange={setNr} t={t} />
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={na} onChange={(e) => setNa(e.target.checked)} />
            {t("admin.task.active")}
          </label>
          <button
            type="button"
            onClick={create}
            disabled={creating || !nt.trim()}
            className="ml-auto h-10 px-5 rounded-[12px] bg-violet-grad text-white font-bold text-sm active:opacity-90 disabled:opacity-60 shadow-violet-btn"
          >
            {creating ? t("admin.task.saving") : t("admin.task.create")}
          </button>
        </div>
      </section>

      {error && <p className="text-sm text-red-400">{error}</p>}

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-extrabold">{t("admin.task.listTitle")}</h2>
        {tasks.map((task) => (
          <TaskEditor key={task.key} task={task} onSaved={load} />
        ))}
      </section>
    </div>
  );
}

function WinnersTab() {
  const { t } = useT();
  const [w, setW] = useState<{ women: WinnerRow[]; men: WinnerRow[] } | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      // Топ-3 считаем из полного лидерборда (там все и уже скрыт vanda03) —
      // иначе, если бэк отдаёт ровно топ-3, после скрытия остаётся лишь 2 строки.
      // Телефоны подтягиваем по нику из /admin/rowing (все) или /admin/winners (топ-3).
      const { leaderboard } = await getLeaderboard();
      const phoneByNick = new Map<string, string>();
      try {
        for (const r of (await adminRowing()).results)
          if (r.nickname && r.phone) phoneByNick.set(r.nickname.toLowerCase(), r.phone);
      } catch {
        /* /admin/rowing может ещё не быть — берём телефоны из winners ниже */
      }
      try {
        const win = await adminWinners();
        for (const r of [...win.women, ...win.men])
          if (r.nickname && r.phone && !phoneByNick.has(r.nickname.toLowerCase()))
            phoneByNick.set(r.nickname.toLowerCase(), r.phone);
      } catch {
        /* нет телефонов — покажем прочерк */
      }
      const top3 = (sex: "F" | "M"): WinnerRow[] =>
        leaderboard
          .filter((r) => r.sex === sex)
          .sort((a, b) => b.power_w - a.power_w)
          .slice(0, 3)
          .map((r, i) => ({
            rank: i + 1,
            nickname: r.nickname,
            phone: (r.nickname && phoneByNick.get(r.nickname.toLowerCase())) || null,
            distance_m: r.distance_m,
            power_w: r.power_w,
            recorded_at: r.recorded_at ?? null,
          }));
      setW({ women: top3("F"), men: top3("M") });
      setError("");
    } catch (e: any) {
      setError(errorMessage(e, t, "admin.task.errLoad"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (error && !w) return <p className="text-red-400 text-sm">{error}</p>;
  if (!w) return <p className="text-muted text-sm">…</p>;

  const Group = ({ title, rows }: { title: string; rows: WinnerRow[] }) => (
    <section>
      <h2 className="text-lg font-extrabold mb-3">{title}</h2>
      <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
        {rows.map((r) => (
          <div key={r.rank} className="flex items-center gap-3 p-4">
            <span className="w-6 text-center text-lg font-extrabold text-violet-lighter">{r.rank}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{r.nickname || "—"}</div>
              {r.phone ? (
                <a href={`tel:${r.phone}`} className="text-xs text-violet-lighter">{r.phone}</a>
              ) : (
                <span className="text-xs text-muted">—</span>
              )}
            </div>
            <span className="text-sm font-semibold w-20 text-right">{Math.round(r.power_w)} W</span>
          </div>
        ))}
        {rows.length === 0 && <div className="p-4 text-muted text-sm">{t("admin.winners.empty")}</div>}
      </div>
    </section>
  );

  return (
    <div className="flex flex-col gap-6">
      <button
        type="button"
        onClick={load}
        className="self-start h-10 px-4 rounded-[12px] bg-white/[0.06] text-white text-sm font-medium active:bg-white/10"
      >
        {t("admin.refresh")}
      </button>
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted2 text-sm">{t("admin.winners.hint")}</p>
        <RefreshButton onRefresh={load} label={t("admin.refresh")} />
      </div>
      <Group title={t("admin.winners.women")} rows={w.women} />
      <Group title={t("admin.winners.men")} rows={w.men} />
    </div>
  );
}

// Полный список участников гребли (пол + телефон) — для сверки на месте.
// Телефоны приходят из /admin/rowing; если бэк его ещё не отдаёт — падаем
// обратно на публичный /leaderboard (без телефонов) и показываем подсказку.
const ROWING_PAGE = 10;

function RowingListTab() {
  const { t } = useT();
  const [rows, setRows] = useState<AdminRowingRow[] | null>(null);
  const [hasPhones, setHasPhones] = useState(true);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setRows((await adminRowing()).results);
      setHasPhones(true);
      setError("");
      return;
    } catch {
      /* бэк ещё без /admin/rowing — ниже публичный лидерборд */
    }
    try {
      setRows((await getLeaderboard()).leaderboard);
      setHasPhones(false);
      setError("");
    } catch (e: any) {
      setError(errorMessage(e, t, "admin.task.errLoad"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (error && !rows) return <p className="text-red-400 text-sm">{error}</p>;
  if (!rows) return <p className="text-muted text-sm">…</p>;

  const sorted = [...rows].sort((a, b) => b.power_w - a.power_w);
  const pages = Math.max(1, Math.ceil(sorted.length / ROWING_PAGE));
  const cur = Math.min(page, pages - 1);
  const slice = sorted.slice(cur * ROWING_PAGE, (cur + 1) * ROWING_PAGE);
  const sexLabel = (s: AdminRowingRow["sex"]) =>
    s === "F" ? t("admin.rowing.f") : s === "M" ? t("admin.rowing.m") : "—";

  const PageNav = () =>
    pages > 1 ? (
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => setPage(Math.max(0, cur - 1))}
          disabled={cur === 0}
          className="h-9 px-4 rounded-[12px] bg-white/[0.06] text-white text-sm font-bold active:bg-white/10 disabled:opacity-40"
        >
          ←
        </button>
        <span className="text-sm text-muted2 font-semibold">
          {t("admin.rowing.page", { p: cur + 1, n: pages })}
        </span>
        <button
          type="button"
          onClick={() => setPage(Math.min(pages - 1, cur + 1))}
          disabled={cur >= pages - 1}
          className="h-9 px-4 rounded-[12px] bg-white/[0.06] text-white text-sm font-bold active:bg-white/10 disabled:opacity-40"
        >
          →
        </button>
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted2 text-sm">{t("admin.rowing.hint", { n: sorted.length })}</p>
        <RefreshButton onRefresh={load} label={t("admin.refresh")} />
      </div>
      {!hasPhones && <p className="text-xs text-muted">{t("admin.rowing.noPhones")}</p>}
      <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
        {slice.map((r, i) => (
          <div key={`${r.nickname}-${cur * ROWING_PAGE + i}`} className="flex items-center gap-3 p-4">
            <span className="w-7 text-center text-sm font-bold text-muted2 shrink-0">
              {cur * ROWING_PAGE + i + 1}
            </span>
            <span
              className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-extrabold shrink-0 ${
                r.sex === "F"
                  ? "bg-[#E85B8A]/[0.18] text-[#F08BAB]"
                  : r.sex === "M"
                    ? "bg-violet-t12 text-violet-lighter"
                    : "bg-white/[0.06] text-muted"
              }`}
            >
              {sexLabel(r.sex)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{r.nickname || "—"}</div>
              {r.phone ? (
                <a href={`tel:${r.phone}`} className="text-xs text-violet-lighter">{r.phone}</a>
              ) : (
                <span className="text-xs text-muted">—</span>
              )}
            </div>
            <span className="text-right shrink-0">
              <span className="block text-sm font-semibold">
                {Math.round(r.power_w)} {t("unit.watts")}
              </span>
              <span className="block text-xs text-muted mt-0.5">
                {Math.round(r.distance_m)} {t("unit.meters")}
              </span>
            </span>
          </div>
        ))}
        {sorted.length === 0 && <div className="p-4 text-muted text-sm">{t("admin.rowing.empty")}</div>}
      </div>
      <PageNav />
    </div>
  );
}

export default function Admin() {
  const { t } = useT();
  const role = useStaffRole();
  const [tab, setTab] = useState<"stats" | "tasks" | "stories" | "rowing" | "winners">("stats");

  if (role !== "admin") {
    // Устройство могло запомнить код другой роли — даём сменить его прямо отсюда.
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-6 text-center">
        <p className="text-muted">{t("admin.forbidden")}</p>
        <button
          type="button"
          onClick={() => {
            clearStaffCode();
            location.reload();
          }}
          className="mt-4 h-11 px-6 rounded-[14px] bg-white/[0.06] text-white font-medium text-sm ring-1 ring-white/10 active:bg-white/10 transition-colors"
        >
          {t("profile.logout")}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-dvh px-6 py-8 max-w-5xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-extrabold tracking-tight">{t("admin.title")}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <LangPills />
            <span className="text-xs font-semibold text-violet-lighter bg-violet-t12 rounded-full px-3 py-1.5">
              {t("staff.youAre", { role: t("staff.role.admin") })}
            </span>
            <button
              type="button"
              onClick={() => {
                clearStaffCode();
                location.reload(); // StaffGate снова спросит код
              }}
              className="h-8 px-3 rounded-full bg-white/[0.06] text-white/70 text-xs font-semibold ring-1 ring-white/10 active:bg-white/10 transition-colors"
            >
              {t("profile.logout")}
            </button>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          {(["stats", "tasks", "stories", "rowing", "winners"] as const).map((tk) => (
            <button
              key={tk}
              type="button"
              onClick={() => setTab(tk)}
              className={`h-11 px-5 rounded-[14px] font-bold text-sm transition-colors ${
                tab === tk ? "bg-violet-grad text-white shadow-violet" : "bg-white/[0.06] text-white active:bg-white/10"
              }`}
            >
              {t(`admin.tab.${tk}`)}
            </button>
          ))}
        </div>
      </header>

      {tab === "stats" ? <StatsTab /> : tab === "tasks" ? <TasksTab /> : tab === "stories" ? <StoryReview /> : tab === "rowing" ? <RowingListTab /> : <WinnersTab />}
    </div>
  );
}
